import React, { useRef } from 'react';
import { throttle } from 'lodash-es';
import { Box, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { withWindow } from '@shared/utils/withWindow';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { useWeddingCodeBannerOffset } from './WeddingCodeBannerOffsetProvider';

export type WeddingCodeBannerSlotProps = Readonly<{
  /** The same copy the banner will render — laid out here, invisibly, to size the reserved space. */
  message: string;
}>;

/**
 * Holds the wedding-code banner's space in normal document flow.
 *
 * The banner itself is `position: fixed` and contributes no layout space. This slot is what page
 * content sits below; it mounts together with the banner (only once eligibility is confirmed —
 * rendering it speculatively painted a visible strip that later collapsed on ineligible events),
 * and the banner lands on top of it rather than covering the page.
 *
 * It sizes itself by laying out the banner's own copy with `visibility: hidden`, at the same width,
 * padding and type scale the banner uses. That makes the reserved height exactly right at every
 * breakpoint, code length and locale — including when the copy wraps to two or three lines on a
 * narrow phone — without measuring anything or clamping the banner. The alternative, a fixed pixel
 * height, forces a choice between truncating long copy and letting the bar overflow the space
 * reserved for it.
 *
 * It paints `pageColor` — the surface the guest site frames the page in (`MainFrame`,
 * `WebsiteHeader`) — so any sliver visible around the banner reads as part of the page. Falls back
 * to `transparent` while the palette is unresolved, which shows whatever is behind rather than
 * committing to a color the theme may not use.
 */
export const WeddingCodeBannerSlot: React.FC<WeddingCodeBannerSlotProps> = ({ message }) => {
  const { pageColor } = useColorPaletteProvider();
  const { setWeddingBannerHeight } = useWeddingCodeBannerOffset();

  // Report the height for the fixed chrome that stacks below the banner (StickyNavVariant,
  // EventMenu). Page layout no longer depends on this number — this element already reserves the
  // space via CSS — so measuring here cannot shift content, and a layout effect keeps the nav
  // offset correct before the browser paints. Re-measure on resize, which is when the copy rewraps.
  const slotRef = useRef<HTMLDivElement>(null);
  useIsomorphicLayoutEffect(() => {
    const element = slotRef.current;
    if (!element) {
      return;
    }
    const updateHeight = () => {
      setWeddingBannerHeight(element.offsetHeight);
    };
    // Initial measure stays unthrottled so the nav offset is right before first paint; only the
    // resize handler is throttled (`offsetHeight` forces layout, and resize fires per frame).
    updateHeight();
    const handleResize = throttle(updateHeight, 100);
    withWindow(global => global.addEventListener('resize', handleResize));
    return () => {
      withWindow(global => global.removeEventListener('resize', handleResize));
      handleResize.cancel();
      setWeddingBannerHeight(0);
    };
  }, [setWeddingBannerHeight, message]);

  return (
    <Box
      ref={slotRef}
      aria-hidden="true"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      paddingX={6}
      paddingY={pxToRem(12)}
      style={{ backgroundColor: pageColor ?? 'transparent' }}
    >
      {/* Hidden, but still laid out — this is what gives the slot its height. Must stay in lockstep
          with the banner's own text props; any divergence shows up as a gap or an overlap. */}
      <TextV2
        typographyVariant={{ _: 'label2', md: 'label3' }}
        textAlign="center"
        style={{ visibility: 'hidden' }}
      >
        {message}
      </TextV2>
    </Box>
  );
};

WeddingCodeBannerSlot.displayName = 'WeddingCodeBannerSlot';
